import _ from 'underscore';
import { OneDSListener } from '@bingads-webui/oneds-listener';
import { ConsoleListener } from '@bingads-webui/console-listener';
import { LocalStorageListener } from '@bingads-webui/local-storage-listener';
import { InMemoryListener, InMemorySink } from '@bingads-webui/in-memory-listener';
import {
  getAriaConfig,
  campaignLogTableConfig,
  adInsightLogTableConfig,
  BasicFormatter,
  ApiFormatter,
  AdInsightOpportunityLogFormatter,
  ReportingApiFormatter,
  ImportApiFormatter,
  ConsoleFormatter,
  LogLevelFilter,
  ApiFilter,
  AdInsightOpportunityApiFilter,
  ConsoleFilter,
  ErrorStormFilter,
  UnloadLogFilter,
  isTestEnvironment,
} from '@bingads-webui-campaign/instrumentation';
import type { GetListenersOptions } from './types';

export function getListeners({
  pageTrackingId,
  applicationName,
  ariaConfig: applicationAriaConfig = {},
}: GetListenersOptions) {
  const currentEnv = document.domain;
  const ariaConfig = {
    ...getAriaConfig(currentEnv),
    ...applicationAriaConfig,
  };

  const applicationSystem = applicationName && applicationName.systemName ?
    applicationName.systemName : 'campaign';
  const applicationSubSystem = applicationName && applicationName.subSystemName ?
    applicationName.subSystemName : 'webui';

  // init log formatters
  const basicFormatter = new BasicFormatter({
    applicationSystem,
    applicationSubSystem,
  });
  const apiFormatter = new ApiFormatter();
  const reportingApiFormatter = new ReportingApiFormatter();
  const importApiFormatter = new ImportApiFormatter();
  const consoleFormatter = new ConsoleFormatter();
  // init log filters
  const logLevelFilter = new LogLevelFilter();
  const apiFilter = new ApiFilter();
  const consoleFilter = new ConsoleFilter();
  const unloadLogFilter = new UnloadLogFilter();
  const errorStormFilter = new ErrorStormFilter();

  const localStorageListener = new LocalStorageListener({
    formatters: {},
    filters: {
      unloadLogFilter,
    },
    uniquePageId: pageTrackingId,
  });

  localStorageListener.init();

  const campaignListener = new OneDSListener({
    tenantToken: ariaConfig.token,
    batchSize: ariaConfig.batchSize,
    logTableNames: campaignLogTableConfig.logTableNames,
    defaultLogCategory: campaignLogTableConfig.defaultLogCategory,
    formatters: {
      basicFormatter,
      apiFormatter,
      reportingApiFormatter,
      importApiFormatter,
    },
    filters: {
      logLevelFilter,
      apiFilter,
      errorStormFilter,
    },
  });

  campaignListener.init();

  const adInsightLogFormatter = new AdInsightOpportunityLogFormatter();
  const adInsightApiFilter = new AdInsightOpportunityApiFilter();

  const adInsightListener = new OneDSListener({
    tenantToken: ariaConfig.tokens.adInsightOppTracking,
    batchSize: ariaConfig.batchSize,
    logTableNames: adInsightLogTableConfig.logTableNames,
    defaultLogCategory: adInsightLogTableConfig.defaultLogCategory,
    formatters: {
      adInsightLogFormatter,
    },
    filters: {
      adInsightApiFilter,
    },
  });

  adInsightListener.init();

  const consoleListener = new ConsoleListener({
    filters: {
      consoleFilter,
    } as unknown as ConstructorParameters<typeof ConsoleListener>[0]['filters'],
    formatters: {
      consoleFormatter,
    },
  });

  consoleListener.init();

  const listeners = {
    localStorageListener,
    campaignListener,
    adInsightListener,
    consoleListener,
  };

  if (isTestEnvironment(currentEnv)) {
    window.seleniumLogsSink = new InMemorySink({ maxSize: 1000 });
    const inMemoryListener = new InMemoryListener({
      sink: window.seleniumLogsSink,
      formatters: {
        basicFormatter,
        apiFormatter,
      },
      filters: {
        logLevelFilter,
        apiFilter,
      },
    });

    inMemoryListener.init();

    _.extend(listeners, { inMemoryListener });
  }

  return listeners;
}
